import { fetchCreateUser, fetchGetUser, fetchUpdateEmail } from '@/services/noteGoApi/noteGoApiComponents';
import { auth } from './firebase';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getAdditionalUserInfo,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  UserCredential,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  linkWithCredential,
} from 'firebase/auth';
import { dripService } from '@/services/dripService';
import * as Sentry from '@sentry/react';
import { FirebaseError } from 'firebase/app';
import { syncAuthToExtension } from '@/utils/extensionAuthSync';
import { sendLogoutToExtension } from '@/utils/extensionCommunication';
import { errorReporter } from '@/services/errorReportingService';
import { logger } from '@/services/loggerService';
import { setLogoutFlag } from '@/utils/crossTabAuthSync';
import { clearQueryCache } from '@/services/queryClient';
import { asApiError, isUserNotFoundError } from '@/utils/apiErrorMessage';
import { DEFAULT_FREE_SUBSCRIPTION } from '@/constants/subscriptionConstants';

declare global {
  interface Window {
    dubAnalytics?: {
      trackLead: (params: {
        eventName: string;
        customerExternalId: string;
        customerEmail?: string;
        customerName?: string;
        clickId?: string | null;
      }) => unknown;
    };
  }
}

/**
 * Read the dub_id cookie value (set by @dub/analytics SDK on referral click)
 */
function getDubIdCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)dub_id=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Track a Dub referral lead event with full diagnostic logging.
 * Fire-and-forget — never blocks the auth flow.
 *
 * Exported so the `signInWithRedirect` fallback path (Login.tsx) can fire it too;
 * that path handles mobile Safari and in-app browsers (CONTRACT #6).
 */
export function trackDubLead(params: {
  customerExternalId: string;
  customerEmail?: string;
  customerName?: string;
  signupMethod: 'email' | 'google';
}): void {
  const dubId = getDubIdCookie();
  const sdkExists = typeof window.dubAnalytics?.trackLead === 'function';

  // CONTRACT #6 requires this path stay traceable in production, so keep
  // `errorReporter.expected` (console in all environments) — but log only
  // booleans. The raw dub_id cookie and the user identifier are PII and must
  // never reach a production console (.claude/rules/security.md).
  errorReporter.expected(
    `Dub referral diagnostic — SDK loaded: ${sdkExists}, dub_id cookie present: ${!!dubId}`,
    undefined,
    {
      feature: 'referral',
      operation: 'trackLead',
      signupMethod: params.signupMethod,
      sdkExists,
      hasDubId: !!dubId,
    },
  );

  if (!sdkExists) {
    errorReporter.expected(
      'Dub SDK not available — window.dubAnalytics.trackLead is not a function. Lead NOT tracked.',
      undefined,
      { feature: 'referral', operation: 'trackLead' },
    );
    return;
  }

  if (!dubId) {
    errorReporter.expected(
      'No dub_id cookie found — user likely did not arrive via referral link. Lead NOT tracked.',
      undefined,
      { feature: 'referral', operation: 'trackLead' },
    );
    return;
  }

  try {
    const trackParams = {
      eventName: 'Sign Up',
      customerExternalId: params.customerExternalId,
      customerEmail: params.customerEmail,
      customerName: params.customerName,
      clickId: dubId, // Explicitly pass dub_id cookie — SDK may not auto-read it
    };

    // Traceable in production (CONTRACT #6) but never logs `trackParams` itself —
    // it carries customerEmail and customerName (.claude/rules/security.md).
    errorReporter.expected('Calling window.dubAnalytics.trackLead()', undefined, {
      feature: 'referral',
      operation: 'trackLead',
      eventName: trackParams.eventName,
      hasEmail: !!trackParams.customerEmail,
      hasName: !!trackParams.customerName,
    });

    const result: unknown = window.dubAnalytics!.trackLead(trackParams);

    // If trackLead returns a promise, capture its outcome without blocking
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<unknown>)
        .then((resolved: unknown) => {
          errorReporter.expected('Dub trackLead() promise resolved successfully', undefined, {
            feature: 'referral',
            operation: 'trackLead',
            // Type only — the resolved payload may carry clickId/customer identifiers (PII).
            resolvedType: typeof resolved,
          });
        })
        .catch((err: unknown) => {
          errorReporter.expected('Dub trackLead() promise REJECTED', err, {
            feature: 'referral',
            operation: 'trackLead',
            // Boolean only — the external id is the Firebase uid (PII).
            hasExternalId: !!params.customerExternalId,
          });
        });
    } else {
      errorReporter.expected('Dub trackLead() returned synchronously (non-promise)', undefined, {
        feature: 'referral',
        operation: 'trackLead',
        // Type only — the return value may carry clickId/customer identifiers (PII).
        resultType: typeof result,
      });
    }
  } catch (syncError: unknown) {
    errorReporter.expected('Dub trackLead() threw synchronously', syncError, {
      feature: 'referral',
      operation: 'trackLead',
      // Boolean only — the external id is the Firebase uid (PII).
      hasExternalId: !!params.customerExternalId,
    });
  }
}

type CaptureContext = Parameters<typeof Sentry.captureException>[1];

let hasWarnedAboutMissingSentryClient = false;

/**
 * Firebase auth error codes that represent expected user input validation failures
 * during sign-up operations. These errors should NOT be reported to Sentry as they
 * are handled by the UI and displayed to the user as validation messages.
 *
 * Note: This only includes sign-up specific errors. Infrastructure errors
 * (network-request-failed, timeout, too-many-requests) are still reported to Sentry
 * for operational monitoring. Sign-in errors are handled separately.
 */
const EXPECTED_SIGNUP_ERROR_CODES = new Set([
  'auth/email-already-in-use',
  'auth/invalid-email',
  'auth/weak-password',
  'auth/missing-email',
  'auth/operation-not-allowed',
  'auth/user-disabled',
]);

/**
 * Checks if a Firebase error is an expected sign-up validation error that should not be reported to Sentry.
 */
const isExpectedSignupError = (error: unknown): boolean => {
  if (error instanceof FirebaseError) {
    return EXPECTED_SIGNUP_ERROR_CODES.has(error.code);
  }
  return false;
};

const isNetworkError = (error: unknown): boolean => {
  const msg = (
    error && typeof error === 'object' && 'message' in error ? String((error as { message: unknown }).message) : ''
  ).toLowerCase();
  return (
    msg.includes('network error') ||
    msg.includes('load failed') ||
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('auth/network-request-failed')
  );
};

const captureAuthException = (error: unknown, context?: CaptureContext) => {
  // Skip reporting expected user input validation errors to Sentry
  if (isExpectedSignupError(error)) {
    return;
  }

  // Skip transient network errors — common on mobile, not actionable
  if (isNetworkError(error)) {
    return;
  }

  if (Sentry.getClient()) {
    // Wrap plain objects so Sentry gets a real Error with a proper stacktrace
    const sentryError =
      error instanceof Error
        ? error
        : new Error(
            error && typeof error === 'object' && 'message' in error
              ? String((error as { message: unknown }).message)
              : 'Unknown auth error',
          );
    Sentry.captureException(sentryError, context);
    return;
  }

  if (!hasWarnedAboutMissingSentryClient) {
    // Sentry itself is the thing that's missing, so this can't be reported to it.
    // Route through logger/errorReporter rather than raw console.* (NEVER #5/#6);
    // errorReporter.expected is console-only by design, which is exactly right here.
    errorReporter.expected('Sentry client is not initialized. Auth errors will not be reported to Sentry.', error, {
      feature: 'auth',
      operation: 'captureAuthException',
    });

    hasWarnedAboutMissingSentryClient = true;
  }
};

// Function to handle storing the token after user login
export const handleUserTokenStorage = async (userCredential: UserCredential) => {
  const token = await userCredential.user.getIdToken(true);
  syncAuthToExtension(userCredential.user, { reason: 'login', token }).catch((err) => {
    errorReporter.expected('Extension auth sync failed on login', err, {
      feature: 'extension',
      operation: 'syncOnLogin',
    });
  });

  // Note: Plan features will be sent by usePlanFeatureSync hook once user data is loaded
  // This ensures we have the latest subscription data before sending to extension

  return token;
};

// Function to create a user and store the token
export const doCreateUserWithEmailAndPassword = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  phoneNumber: string,
) => {
  // Import authStore to set the isSigningUp flag
  // This prevents onAuthStateChanged from calling fetchCreateUser with empty names
  const { useAuthStore } = await import('@/stores/authStore');
  const { setIsSigningUp } = useAuthStore.getState();

  // Set flag BEFORE creating Firebase user to prevent race condition
  setIsSigningUp(true);

  let userCredential: UserCredential;

  try {
    userCredential = await createUserWithEmailAndPassword(auth, email, password);
  } catch (error) {
    // Clear the flag on error since signup failed
    setIsSigningUp(false);

    const extra: Record<string, unknown> = {
      context: 'creating firebase user',
      errorCode: error instanceof FirebaseError ? error.code : 'unknown',
    };

    if (email.includes('@')) {
      extra.emailDomain = email.split('@')[1];
    }

    captureAuthException(error, {
      tags: { feature: 'auth' },
      extra,
    });

    throw error;
  }

  // CONTRACT #6: fire referral tracking as soon as the Firebase account exists,
  // before any backend work. Keeping it after `await fetchCreateUser` meant a
  // backend throw skipped tracking entirely — the exact failure mode CONTRACT #6
  // was written to eliminate. Fire-and-forget; never blocks signup.
  trackDubLead({
    customerExternalId: userCredential.user.uid,
    customerEmail: email,
    customerName: `${firstName} ${lastName}`.trim(),
    signupMethod: 'email',
  });

  try {
    const token = await handleUserTokenStorage(userCredential); // Store the token

    const user = userCredential.user;
    await updateProfile(user, {
      displayName: `${firstName} ${lastName}`,
    });

    // NOTE: Do NOT mirror the name into Firestore here. CONTRACT #5 — Firestore
    // `users` docs are keyed by the backend user ID (`userData.id`), never the
    // Firebase Auth UID, and that ID does not exist yet at this point.
    // `fetchCreateUser` below persists first/last name via the backend, which
    // owns the Firestore document.

    // Create user with firebase_uid to prevent duplicates
    try {
      await fetchCreateUser({
        headers: { authorization: `Bearer ${token}` },
        body: {
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
          firebase_uid: user.uid, // Pass Firebase UID to prevent duplicate user documents
          // Include default subscription to prevent AI processing failures
          // Backend requires subscription.plan to be set for AI worker to process media
          subscription: DEFAULT_FREE_SUBSCRIPTION,
        },
      });

      // Update the email separately since UserCreate doesn't have an email field
      try {
        await fetchUpdateEmail({
          headers: { authorization: `Bearer ${token}` },
          body: {
            email: email,
          },
        });
      } catch (emailError) {
        // Log error but don't fail the signup - email can be updated later
        captureAuthException(emailError, {
          tags: { feature: 'auth' },
          extra: { context: 'updating email in backend during signup' },
        });
      }
    } catch (createError) {
      // If user creation fails, we should still return the Firebase credential
      // The authStore will retry creating the backend user
      captureAuthException(createError, {
        tags: { feature: 'auth' },
        extra: { context: 'creating user in backend during signup' },
      });
    }

    // Clear the signup flag BEFORE initializing user to allow onIdTokenChanged to process
    // any token refresh events that occur during initialization
    setIsSigningUp(false);

    // Now that signup is complete with all required steps, initialize the user in authStore
    // This must be called on the successful path only to avoid leaving UI in logged-in state on failure
    const { initializeUser } = useAuthStore.getState();
    initializeUser(userCredential.user);

    return userCredential;
  } catch (error) {
    // Clear the flag on any error in the try block
    setIsSigningUp(false);
    throw error;
  }
};

// Function to sign in with email and password and store the token
export const doSignInWithEmailAndPassword = async (email: string, password: string) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  await handleUserTokenStorage(userCredential); // Store the token
  return userCredential;
};

// Function to sign in with Google and store the token
// Returns both the UserCredential and whether this is a newly created user
export const doSignInWithGoogle = async (): Promise<{
  result: UserCredential | null;
  isNewUser: boolean;
  backendUserId: string | null;
}> => {
  const provider = new GoogleAuthProvider();
  let isNewUser = false;

  // Import authStore to set the isSigningUp flag
  // This prevents onAuthStateChanged from calling fetchCreateUser before we can
  // detect whether this is a new user, which would cause isNewUser to always be false
  const { useAuthStore } = await import('@/stores/authStore');
  const { setIsSigningUp } = useAuthStore.getState();

  // Set flag BEFORE signInWithPopup to prevent race condition where
  // onAuthStateChanged fires and creates the backend user before we can check
  setIsSigningUp(true);

  try {
    // Try popup first (better UX when it works)
    const result = await signInWithPopup(auth, provider);

    // Track Dub referral lead IMMEDIATELY for new users using Firebase's authoritative signal.
    // This must fire before backend operations — the old approach buried trackDubLead inside
    // fetchGetUser→404→fetchCreateUser, where any error in that chain silently killed tracking.
    const additionalInfo = getAdditionalUserInfo(result);
    if (additionalInfo?.isNewUser) {
      trackDubLead({
        customerExternalId: result.user.uid,
        customerEmail: result.user.email || undefined,
        customerName: result.user.displayName || undefined,
        signupMethod: 'google',
      });
    }

    const token = await handleUserTokenStorage(result);

    // Check/create user in backend
    let backendUserData: { id?: string } | null = null;
    try {
      backendUserData = await fetchGetUser({ headers: { authorization: `Bearer ${token}` } });
    } catch (error: unknown) {
      if (isUserNotFoundError(error)) {
        // User doesn't exist in backend - this is a new user
        // Set flag early so new users go through onboarding even if backend creation fails
        isNewUser = true;

        const googleFirstName = result?.user?.displayName?.split(' ')[0] || '';
        const googleLastName = result?.user?.displayName?.split(' ').slice(1).join(' ') || '';

        try {
          await fetchCreateUser({
            headers: { authorization: `Bearer ${token}` },
            body: {
              first_name: googleFirstName,
              last_name: googleLastName,
              phone_number: '',
              firebase_uid: result.user.uid, // Pass Firebase UID to prevent duplicate user documents
              // Include default subscription to prevent AI processing failures
              // Backend requires subscription.plan to be set for AI worker to process media
              subscription: DEFAULT_FREE_SUBSCRIPTION,
            },
          });

          // NOTE: Do NOT mirror the name into Firestore here — CONTRACT #5.
          // `fetchCreateUser` above already persisted first/last name through the
          // backend, which owns the Firestore `users/{userData.id}` document.

          // Update the email for Google sign-in users as well
          if (result.user.email) {
            try {
              await fetchUpdateEmail({
                headers: { authorization: `Bearer ${token}` },
                body: {
                  email: result.user.email,
                },
              });
            } catch (emailError) {
              // Log error but don't fail the sign-in - email can be updated later
              captureAuthException(emailError, {
                tags: { feature: 'auth' },
                extra: { context: 'updating email in backend during Google sign-in' },
              });
            }
          }
        } catch (createError) {
          // If user creation fails, log but still return the result
          // The authStore will retry creating the backend user
          captureAuthException(createError, {
            tags: { feature: 'auth' },
            extra: { context: 'creating user in backend during Google sign-in' },
          });
        }

        // Clear signup flag and initialize user now that backend user is created
        setIsSigningUp(false);
        const { initializeUser } = useAuthStore.getState();
        initializeUser(result.user);

        return { result, isNewUser, backendUserId: null };
      } else {
        // Non-404 error (transient 500, network timeout, etc.)
        // Firebase auth succeeded, so initialize the user and let authStore's
        // fetchAndSetUserData retry the backend call (it has create-if-not-found logic)
        captureAuthException(error, {
          tags: { feature: 'auth' },
          extra: { context: 'fetchGetUser non-404 error during Google sign-in', status: asApiError(error).status },
        });
        setIsSigningUp(false);
        const { initializeUser } = useAuthStore.getState();
        initializeUser(result.user);

        // Identity is UNKNOWN here — we never learned whether a backend user
        // exists. Firebase's own isNewUser signal is authoritative, so use it:
        // reporting `isNewUser: false` for an actual new account routed them
        // straight to HOME and skipped onboarding permanently (no route-level
        // guard re-checks it).
        return { result, isNewUser: !!additionalInfo?.isNewUser, backendUserId: null };
      }
    }
    // Existing user - fetchGetUser succeeded
    // Clear signup flag and initialize user for existing users
    setIsSigningUp(false);
    const { initializeUser } = useAuthStore.getState();
    initializeUser(result.user);

    return { result, isNewUser: false, backendUserId: backendUserData?.id || null };
  } catch (popupError: unknown) {
    // Clear the flag on any error
    setIsSigningUp(false);

    const popupErr = asApiError(popupError);
    const popupErrMessage = typeof popupErr.message === 'string' ? popupErr.message : '';

    // If popup fails due to COOP or popup blockers, fall back to redirect
    if (
      popupErr.code === 'auth/popup-blocked' ||
      popupErr.code === 'auth/popup-closed-by-user' ||
      popupErr.code === 'auth/cancelled-popup-request' ||
      popupErrMessage.includes('Cross-Origin-Opener-Policy')
    ) {
      // Use redirect flow as fallback
      await signInWithRedirect(auth, provider);
      // This will redirect away from the page, so no return value
      return { result: null, isNewUser: false, backendUserId: null };
    }

    // Re-throw other errors
    throw popupError;
  }
};

// Function to sign out and remove the token from chrome.storage
export const doSignOut = async () => {
  // Set logout flag in sessionStorage IMMEDIATELY (before any async work)
  // Uses localStorage for cross-tab synchronization - other tabs will receive
  // a 'storage' event and can react to the logout
  sessionStorage.setItem('userRequestedLogout', 'true');
  setLogoutFlag();

  // Import auth store to set logout flag - prevents race condition with extension auth
  const { useAuthStore } = await import('@/stores/authStore');

  // Set logout flag in store to block any concurrent auth operations
  useAuthStore.getState().setIsLoggingOut(true);

  logger.debug('auth', 'Logout initiated - blocking auth operations');
  // Notify Chrome extension to clear its auth state
  sendLogoutToExtension();

  // Clear Drip tagging cache on logout
  dripService.clearTaggedUsersCache();

  // Purge the React Query cache. Generated query keys carry no viewer identity,
  // and logout is SPA navigation — without this, the next account to log in on
  // this tab is served the previous user's cached notes/user/subscription data
  // with no network call. Generalizes CONTRACT #8 to every generated hook.
  clearQueryCache();

  // Clear any stored redirect paths upon manual sign-out to ensure a clean login experience next time.
  sessionStorage.removeItem('redirectAfterLogin');

  try {
    // IMPORTANT: Do NOT reset isLoggingOut in a finally block or immediately after signOut.
    // The onAuthStateChanged listener in authStore.ts will reset it when it receives
    // the null user (confirming logout is complete). This prevents a race condition
    // where extension auto-login could re-authenticate before auth state was cleared.
    await auth.signOut();
    // Success: onAuthStateChanged will fire with null user and reset isLoggingOut
  } catch (error) {
    // If signOut fails, we must reset isLoggingOut to allow retry attempts
    // Without this, the flag would remain true forever, blocking all auth operations
    useAuthStore.getState().setIsLoggingOut(false);
    // Clear the durable sessionStorage flag too. `isLogoutInProgress()` reads it
    // and it has no expiry, so leaving it set after a failed sign-out would kill
    // extension sign-in and silent auto-login for the rest of the tab session
    // with no recovery path (the old localStorage-only check self-expired in 10s).
    sessionStorage.removeItem('userRequestedLogout');
    throw error; // Re-throw so callers can handle it if needed
  }
};

// Function to send a password reset email
export const doSendPasswordResetEmail = async (email: string) => {
  // This should make Firebase use its default action handler
  return sendPasswordResetEmail(auth, email);
};

// Function to update password directly (requires recent authentication)
export const doUpdatePassword = async (newPassword: string) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user is currently logged in');
  }
  return updatePassword(user, newPassword);
};

// Function to reauthenticate user before sensitive operations
export const doReauthenticateWithPassword = async (email: string, password: string) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user is currently logged in');
  }
  const credential = EmailAuthProvider.credential(email, password);
  return reauthenticateWithCredential(user, credential);
};

// Function to check if user has password provider
export const hasPasswordProvider = () => {
  const user = auth.currentUser;
  if (!user) return false;

  return user.providerData.some((provider) => provider.providerId === 'password');
};

// Function to add password to Google auth account
export const doLinkWithPassword = async (password: string) => {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('No user is currently logged in or user has no email');
  }

  const credential = EmailAuthProvider.credential(user.email, password);
  return linkWithCredential(user, credential);
};
